// TAS-199: keep the browser's own chrome in sync with the screen it frames.
//
// Two inputs decide the colour of the bars above and below the page, and both
// have to agree or the seam shows:
//
//   1. <meta name="theme-color">  — iOS Safari 15+, Chrome (iOS and Android),
//      Edge and Samsung Internet tint their toolbars from it.
//   2. html/body background-color — Safari paints the overscroll (rubber-band)
//      area and the region behind the safe-area insets from it.
//
// This module owns both. The default (cream) lives in index.html's theme-color
// meta and index.css's html/body rules; any screen that paints something else
// full-bleed declares its colour with the useChromeTheme hook.
//
// An earlier version of this file assumed "Chrome doesn't tint", so the rollout
// stopped at a handful of screens and every other full-bleed screen kept the
// cream default — white bars around a dark screen. Chrome tints too.
//
// A STACK, not a single value: the transition curtain portals in on top of a
// screen that has already declared its own colour, and when the curtain leaves
// the chrome has to return to THAT colour rather than to the cream default.
// Each caller pushes on mount and pops on unmount; the top of the stack wins.

/** The chrome colours, one per full-bleed surface in the app (Design.md). */
export const CHROME = {
  /** wine-background / tasted-cream — the app default, in index.html + index.css. */
  cream: '#F9F6F1',
  /** wine-primary / tasted-green-deep — every full-bleed green overlay. */
  green: '#1C3B29',
  /** The join screen's deeper green. */
  joinDeep: '#0F1A13',
  /** The scoreboard blackout — near-black, and the only screen that uses it. */
  blackout: '#12100F',
  /**
   * The reveal countdown veil on Results: `bg-wine-primary/90` composited over
   * `bg-wine-background`. Pre-blended, because the bars are opaque and would
   * otherwise read a shade darker than the screen they frame.
   */
  countdownVeil: '#324E3D',
};

const DEFAULT_CHROME = CHROME.cream;

// [{ id, color }] — last entry wins.
const stack = [];
let nextId = 1;

function metaEl() {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  return meta;
}

function apply() {
  try {
    const color = stack.length ? stack[stack.length - 1].color : DEFAULT_CHROME;
    metaEl().setAttribute('content', color);
    // Inline rather than a utility class: an `!important` stylesheet rule would
    // beat these, which is exactly the trap the old .bg-overlay-green class set.
    document.documentElement.style.backgroundColor = color;
    document.body.style.backgroundColor = color;
  } catch {
    /* non-browser runtime — no-op */
  }
}

/**
 * Claim the chrome for a colour. Returns a token to hand back to
 * `popChromeTheme`. Prefer the `useChromeTheme` hook — this is the layer under
 * it, exported for the rare caller that isn't a component.
 */
export function pushChromeTheme(color) {
  const id = nextId++;
  stack.push({ id, color });
  apply();
  return id;
}

/** Release a claim. Safe to call twice; the chrome falls back to whatever is
 *  still on the stack, or to the cream default when nothing is. */
export function popChromeTheme(id) {
  const i = stack.findIndex((entry) => entry.id === id);
  if (i === -1) return;
  stack.splice(i, 1);
  apply();
}

/** Test seam — drops every claim and repaints the default. */
export function __resetChromeThemeStack() {
  stack.length = 0;
  apply();
}
